/**
 * InviteCodesScreen — mint 8-char alphanumeric invite codes.
 *
 * Codes are bound to a role (member | admin | billing_admin),
 * optional pre-filled email, and a 14-day expiration. The admin
 * copies the code + shareable URL into the new SLP's onboarding
 * email/Slack.
 *
 * The plain code is the secret; the URL is `https://app.effectiveslp.com/join/{code}`.
 * The web SPA's join flow (future work) consumes the code via a
 * `redeem_invite_code` RPC that sets organization_memberships on
 * the redeeming user.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../../lib/supabase/client';
import { DataTable, type DataTableColumn } from '../../components/DataTable';

interface InviteRow {
  id: string;
  code: string;
  role: 'member' | 'admin' | 'billing_admin';
  email: string | null;
  created_at: string;
  expires_at: string;
  used_at: string | null;
}

interface Props {
  orgId: string;
}

/**
 * 8-char URL-safe code from crypto.getRandomValues (browser).
 * 192 bits of entropy is overkill for a 14-day code but matches
 * the same generator used for the hand-off tokens (Slice 3).
 */
function generateInviteCode(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i] ?? 0);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '').slice(0, 8);
}

export function InviteCodesScreen({ orgId }: Props): ReactElement {
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [minting, setMinting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState<'member' | 'admin' | 'billing_admin'>('member');
  const [email, setEmail] = useState('');
  const [lastMinted, setLastMinted] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    setLoading(true);
    const { data, error: e } = await supabase
      .from('organization_invites')
      .select('id, code, role, email, created_at, expires_at, used_at')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })
      .limit(100);
    if (e) {
      setError(e.message);
    } else {
      setInvites((data ?? []) as InviteRow[]);
    }
    setLoading(false);
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const inviteColumns: DataTableColumn<InviteRow>[] = [
    {
      header: 'Code',
      isRowHeader: true,
      cell: (i) => <code>{i.code}</code>,
    },
    { header: 'Role', cell: (i) => i.role },
    { header: 'Email', cell: (i) => i.email ?? '—' },
    {
      header: 'Created',
      cell: (i) => new Date(i.created_at).toLocaleDateString(),
    },
    {
      header: 'Expires',
      cell: (i) => new Date(i.expires_at).toLocaleDateString(),
    },
    {
      header: 'Used',
      cell: (i) =>
        i.used_at ? new Date(i.used_at).toLocaleDateString() : '—',
    },
    {
      header: 'Actions',
      cell: (i) => (
        <button
          type="button"
          className="link-button"
          onClick={() => void handleCopy(i.code)}
        >
          Copy link
        </button>
      ),
    },
  ];

  async function handleMint(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setMinting(true);
    setError(null);
    try {
      const code = generateInviteCode();
      const expiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error: iErr } = await supabase
        .from('organization_invites')
        .insert({
          organization_id: orgId,
          code,
          role,
          email: email.trim() === '' ? null : email.trim(),
          expires_at: expiresAt,
        })
        .select('code')
        .single();
      if (iErr) {
        setError(iErr.message);
        return;
      }
      setLastMinted((data as { code: string }).code);
      setEmail('');
      await refresh();
    } finally {
      setMinting(false);
    }
  }

  async function handleCopy(code: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(
        `https://app.effectiveslp.com/join/${code}`,
      );
    } catch {
      // Clipboard can be denied in private windows; fall back to a
      // text selection hint.
      setError('Copy denied. Select the code manually.');
    }
  }

  return (
    <div className="admin-invites">
      <h2>Invite codes</h2>
      <form onSubmit={(e) => void handleMint(e)} className="invite-form">
        <label>
          Role
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as typeof role)}
          >
            <option value="member">SLP (member)</option>
            <option value="admin">District admin</option>
            <option value="billing_admin">Billing admin</option>
          </select>
        </label>
        <label>
          Email (optional, for tracking)
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="new-hire@example.com"
          />
        </label>
        <button type="submit" className="snap-button" disabled={minting}>
          {minting ? 'Minting…' : 'Mint invite code'}
        </button>
        {lastMinted && (
          <p className="success">
            Minted code{' '}
            <code>{lastMinted}</code> — valid for 14 days.
          </p>
        )}
      </form>

      {error && <p className="error">{error}</p>}

      <h3>Active + recent invites</h3>
      {loading ? (
        <p className="tagline">Loading…</p>
      ) : invites.length === 0 ? (
        <p className="tagline">No invites yet.</p>
      ) : (
        <DataTable<InviteRow>
          caption="Invite codes"
          ariaLabel="Organization invite codes"
          rowKey={(i) => i.id}
          columns={inviteColumns}
          rows={invites}
        />
      )}
    </div>
  );
}